import { useTheme } from "@emotion/react";
import {
  Cancel,
  CheckCircle,
  Clear,
  ErrorOutlineOutlined,
  ExpandMore,
  Search,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  CircularProgress,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import DatagridComponent from "../../../../../../components/datagridComponent";
import api from "../../../../../../services/api";
import ErpChip from "../../../../../../components/erp/ErpChip";
import ErpFilter from "../../../../../../components/erp/ErpFilter";
import useErpSelecionado from "../../../../../../components/erp/useErpSelecionado";
import useStyles from "./styles";

export default function ConfigRfo() {
  const theme = useTheme();
  const styles = useStyles(theme);
  // Os motivos de fechamento são de cada HubSoft (HUBSOFT A e B têm a sua
  // configuração): a tela mostra e salva a do ERP escolhido.
  const { erps, erpId, setErpId, carregado, variosErps } = useErpSelecionado([
    "HUBSOFT",
  ]);
  const erpSelecionado = erps?.find((item) => item.id === erpId);
  const erpAtualRef = useRef(erpId);
  erpAtualRef.current = erpId;
  // Busca só depois da primeira abertura do accordion (e de novo ao trocar de ERP).
  const [jaAberto, setJaAberto] = useState(false);

  const [loading, setLoading] = useState(false);

  const [configTicket, setConfigTicket] = useState({});

  const [buscarTipoEncerramento, setBuscarTipoEncerramento] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [configTicketChange, setConfigTicketChange] = useState({});
  const [motivosFechamento, setMotivosFechamento] = useState([]);
  const [configRfo, setConfigRfo] = useState({
    requiredRfo: false,
    config: {},
  });
  useEffect(() => {
    if (!carregado || !jaAberto) return;
    setIsEdit(false);
    getConfigRfo();
  }, [carregado, erpId, jaAberto]);

  const getConfigRfo = async () => {
    const erpPedido = erpId;
    setLoading(true);
    try {
      const response = await api.post("/interno/find/configRfo", {
        erpId: erpPedido,
      });
      if (erpPedido !== erpAtualRef.current) return; // trocou de ERP no meio
      setMotivosFechamento(response?.data?.motivosFechamento);
      setConfigRfo(response?.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const updateConfigRfo = async (idConfigoRfo, requiredRfo, config) => {
    setLoadingUpdate(true);
    try {
      const response = await api.post("/interno/update/configRfo", {
        erpId,
        idConfigoRfo: idConfigoRfo,
        configRfo: {
          requiredRfo: requiredRfo,
          config: config,
        },
      });

      setConfigRfo(response?.data);

      setSnackbar({
        children: "Configuração de RFO atualizada com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.log(error);
      setSnackbar({
        children: "Erro ao atualizar configuração de RFO!",
        severity: "error",
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  const filteredRows =
    configRfo?.config?.requiredRfoForMotivoFechamento?.filter((row, index) => {
      row.numero = index;

      return row.descricao
        ?.toLowerCase()
        ?.includes(buscarTipoEncerramento?.toLowerCase()?.trim());
    });

  /*   const filteredRows = []; */
  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "descricao",
      headerName: "Motivo de encerramento",
      flex: 1,
    },
    {
      field: "requiredRfo",
      headerName: "RFO obrigatório",
      width: 150,
      renderCell: (params) => (
        <Switch
          disabled={loadingUpdate}
          size="small"
          checked={params.row.requiredRfo}
          onChange={(e) => {
            const newConfigForMotivoFechamento =
              configRfo?.config?.requiredRfoForMotivoFechamento?.map((item) => {
                if (item.id === params.row.id) {
                  item.requiredRfo = e.target.checked;
                }
                return item;
              });

            const newConfig = {
              ...configRfo?.config,
              requiredRfoForMotivoFechamento: newConfigForMotivoFechamento,
            };

            updateConfigRfo(configRfo?.id, false, newConfig);
          }}
          focusVisibleClassName=".Mui-focusVisible"
          disableRipple
        />
      ),
    },
  ];
  return (
    <>
      <Accordion data-secao="rfo" onChange={(_, aberto) => aberto && setJaAberto(true)}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box sx={styles.containerHeader}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ErrorOutlineOutlined color="primary" />
              <Typography
                variant="h5"
                sx={{
                  ...styles.title,
                }}
              >
                Configurações de RFO
              </Typography>
              {/* Para qual ERP é esta configuração. */}
              <ErpChip erp={erpSelecionado || { tipo: "HUBSOFT" }} />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {variosErps && (
            <ErpFilter
              erps={erps}
              value={erpId}
              onChange={(id) => id && setErpId(id)}
              limpavel={false}
              label="HubSoft"
              id="filtro-erp-rfo"
              sx={{ minWidth: 260, maxWidth: 360, mb: 1.5 }}
            />
          )}
          <Paper variant="outlined" sx={styles.paperBody}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={loadingUpdate}
                            sx={styles.switch}
                            checked={configRfo?.requiredRfo}
                            onChange={(e) => {
                              setConfigRfo((prev) => ({
                                ...prev,
                                requiredRfo: e.target.checked,
                              }));
                              updateConfigRfo(
                                configRfo?.id,
                                e.target.checked,
                                configRfo?.config,
                              );
                            }}
                            checkedIcon={<CheckCircle sx={styles.icon} />}
                            icon={<Cancel sx={styles.icon} />}
                            focusVisibleClassName=".Mui-focusVisible"
                            disableRipple
                          />
                        }
                        sx={{ margin: 0 }}
                      />
                    </FormGroup>

                    <Box>
                      <Typography data-busca="rfo-todos" variant="body2" sx={styles.titleSwitch}>
                        Obrigatoriedade de RFO no encerramento de todos os
                        tickets
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "10px",
                          color: theme.palette.text.secondary,
                          ml: 1,
                        }}
                      >
                        Quando ativado, é necessário adicionar o RFO para todos
                        os tickets no encerramento
                      </Typography>
                    </Box>
                  </Box>
                  {!configRfo?.requiredRfo && (
                    <TextField
                      label="Buscar motivo encerramento"
                      data-busca="rfo-motivo"
                      variant="outlined"
                      autoComplete="off"
                      placeholder="Motivo de encerramento"
                      value={buscarTipoEncerramento}
                      size="small"
                      onChange={(event) => {
                        setBuscarTipoEncerramento(
                          event?.target?.value?.trimStart(),
                        );
                      }}
                      sx={styles.searchField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                        endAdornment: buscarTipoEncerramento ? (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setBuscarTipoEncerramento("")}
                              edge="end"
                            >
                              <Clear size={"small"} />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  )}
                </Box>
                <Collapse in={!configRfo?.requiredRfo}>
                  {" "}
                  <Paper
                    variant="outlined"
                    elevation={3}
                    sx={styles.paperDatagrid}
                  >
                    <DatagridComponent columns={columns} rows={filteredRows} />
                  </Paper>
                </Collapse>
              </>
            )}
          </Paper>
        </AccordionDetails>
      </Accordion>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
